import React, { createContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, googleProvider } from '../firebase';
import { syncAuthWithExtension, syncLogoutWithExtension, syncSessionTokenWithExtension } from '../utils/extensionSync';
import { initializeExtensionListener } from '../utils/extensionListener';

export const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign up with email and password
  const signup = async (email, password, displayName) => {
    try {
      setError(null);

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name in Firebase Auth
      await updateProfile(user, { displayName });

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: displayName,
        createdAt: serverTimestamp(),
        plan: 'free'
      });

      // Initialize default settings and pipeline
      await setDoc(doc(db, 'users', user.uid, 'settings', 'preferences'), {
        gmailConnected: false,
        gmailEmail: null,
        autoLogEmails: false,
        emailTracking: false,
        notifications: {
          dealUpdates: true,
          newEmails: true,
        },
        pipelines: [{
          id: 'default',
          name: 'Sales Pipeline',
          isDefault: true,
          stages: [
            { position: 0, name: 'Contacts', color: '#6B7280' },
            { position: 1, name: 'Proposal', color: '#3B82F6' },
            { position: 2, name: 'Negotiation', color: '#F59E0B' },
            { position: 3, name: 'Won', color: '#10B981' },
            { position: 4, name: 'Lost', color: '#EF4444' },
          ],
          createdAt: new Date(),
          updatedAt: new Date(),
        }],
        updatedAt: serverTimestamp()
      });

      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Create session token after successful login
      await createSessionToken();

      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;

      // Create session token after successful login
      await createSessionToken();

      // Check if user document exists, create if not
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        console.log('[AuthContext] Creating user document for new Google user');
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
          plan: 'free'
        });

        // Initialize default settings and pipeline
        await setDoc(doc(db, 'users', user.uid, 'settings', 'preferences'), {
          gmailConnected: false,
          gmailEmail: null,
          autoLogEmails: false,
          emailTracking: false,
          notifications: {
            dealUpdates: true,
            newEmails: true,
          },
          pipelines: [{
            id: 'default',
            name: 'Sales Pipeline',
            isDefault: true,
            stages: [
              { position: 0, name: 'Contacts', color: '#6B7280' },
              { position: 1, name: 'Proposal', color: '#3B82F6' },
              { position: 2, name: 'Negotiation', color: '#F59E0B' },
              { position: 3, name: 'Won', color: '#10B981' },
              { position: 4, name: 'Lost', color: '#EF4444' },
            ],
            createdAt: new Date(),
            updatedAt: new Date(),
          }],
          updatedAt: serverTimestamp()
        });
      } else {
        // User exists - update photo from Google if available
        if (user.photoURL) {
          console.log('[AuthContext] Updating user photo from Google');
          await setDoc(doc(db, 'users', user.uid), {
            photoURL: user.photoURL
          }, { merge: true });
        }
      }

      return user;
    } catch (err) {
      // Handle account-exists-with-different-credential error
      // TODO: Implement automatic account linking (backlog issue)
      // For now, show clear error message to user
      if (err.code === 'auth/account-exists-with-different-credential') {
        console.error('[AuthContext] Account exists with different credential');

        const email = err.customData?.email;

        // Check what sign-in methods exist for this email
        const methods = await fetchSignInMethodsForEmail(auth, email);
        console.log('[AuthContext] Existing sign-in methods:', methods);

        // Show user-friendly error message
        if (methods.includes('password')) {
          setError(
            'An account with this email already exists. Please sign in with your email and password instead.'
          );
        } else if (methods.includes('google.com')) {
          setError(
            'An account with this email already exists. Please sign in with Google instead.'
          );
        } else {
          setError(
            `An account with this email already exists with a different sign-in method (${methods.join(', ')}). ` +
            `Please use that method to sign in.`
          );
        }
        throw err;
      } else {
        setError(err.message);
        throw err;
      }
    }
  };

  // Create session token
  const createSessionToken = async () => {
    try {
      const authCreateSession = httpsCallable(functions, 'authCreateSession');
      const response = await authCreateSession({});

      if (response.data.success) {
        const { sessionToken, expiresAt } = response.data;

        // Store session token in localStorage
        localStorage.setItem('workstack_session_token', sessionToken);
        localStorage.setItem('workstack_session_expires', expiresAt.toString());

        console.log('[AuthContext] Session token created, expires:', new Date(expiresAt).toISOString());

        // Sync session token with extension (pass current user for email info)
        const currentUser = auth.currentUser;
        await syncSessionTokenWithExtension(sessionToken, expiresAt, currentUser);
      }
    } catch (err) {
      console.error('[AuthContext] Error creating session token:', err);
      // Don't throw - session creation failure shouldn't block login
    }
  };

  // Logout
  const logout = async () => {
    try {
      setError(null);

      // Revoke session token
      await revokeSessionToken();

      // Sync logout with Chrome Extension
      await syncLogoutWithExtension();

      // Sign out from Firebase
      await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Revoke session token
  const revokeSessionToken = async () => {
    try {
      const authRevokeSession = httpsCallable(functions, 'authRevokeSession');
      await authRevokeSession({});

      // Remove session token from localStorage
      localStorage.removeItem('workstack_session_token');
      localStorage.removeItem('workstack_session_expires');

      console.log('[AuthContext] Session token revoked');
    } catch (err) {
      console.error('[AuthContext] Error revoking session token:', err);
      // Don't throw - session revocation failure shouldn't block logout
    }
  };

  // Get user profile from Firestore
  const getUserProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data();
      }
      return null;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        const profile = await getUserProfile(user.uid);
        setCurrentUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          ...profile
        });

        // Check if session token exists and is valid
        const sessionToken = localStorage.getItem('workstack_session_token');
        const sessionExpires = localStorage.getItem('workstack_session_expires');

        if (!sessionToken || !sessionExpires || Date.now() >= parseInt(sessionExpires)) {
          // No session or expired - create new session
          console.log('[AuthContext] No valid session, creating new one');
          await createSessionToken();
        } else {
          // Valid session exists - sync with extension (pass user for email info)
          console.log('[AuthContext] Valid session exists');
          await syncSessionTokenWithExtension(sessionToken, parseInt(sessionExpires), user);
        }

        // Sync authentication with Chrome Extension
        await syncAuthWithExtension(user);
      } else {
        // User is signed out
        setCurrentUser(null);
      }
      setLoading(false);
    });

    // Initialize listener for extension messages
    initializeExtensionListener(
      // onLogout callback
      async () => {
        console.log('[AuthContext] Logout requested by extension');
        await signOut(auth);
      },
      // onSignIn callback
      async (data) => {
        console.log('[AuthContext] Sign-in notification from extension:', data);

        // Extension has already authenticated with Firebase and has a session token
        // We just need to save the session token locally to sync state
        if (data.sessionToken && data.expiresAt) {
          localStorage.setItem('workstack_session_token', data.sessionToken);
          localStorage.setItem('workstack_session_expires', data.expiresAt.toString());
          console.log('[AuthContext] Session token synced from extension');
        }

        // Firebase auth state will be automatically synced by onAuthStateChanged
        // when the extension signs in (they share the same Firebase instance)
      }
    );

    // Cleanup subscription
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    error,
    signup,
    login,
    signInWithGoogle,
    logout,
    getUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
